import * as XLSX from 'xlsx';

// 解析出的单行数据
export interface SmsRow {
  // 原始行号（Excel 第几行，含表头，用于定位问题）
  rowNo: number;
  // 区域原始文本
  regionRaw: string;
  // 解析后的国家区号；无法解析为 null
  countryCode: number | null;
  // 手机号（已去空格）
  phone: string;
  // 短信内容（行内无内容列时为空，发送时回退到页面统一内容）
  message: string;
  // 行级校验错误（为空表示通过）
  error: string;
}

// 列名匹配关键词（小写、去空格后 includes 命中即可）
const PHONE_KEYS = ['手机', '号码', 'phone', 'mobile', 'tel'];
const REGION_KEYS = ['区域', '区号', '国家', '地区', 'region', 'country', 'area', 'code'];
const MESSAGE_KEYS = ['内容', '短信', '正文', 'message', 'content', 'text', 'sms'];

// 常见地区名 → 国家区号。命中不了的（纯数字/未知名）走数字解析或判为非法。
const REGION_NAME_TO_CODE: Record<string, number> = {
  美国: 1,
  美: 1,
  us: 1,
  usa: 1,
  加拿大: 1,
  中国: 86,
  中: 86,
  cn: 86,
  china: 86,
  墨西哥: 52,
  墨: 52,
  mx: 52,
  mexico: 52,
};

const norm = (v: unknown): string => String(v ?? '').trim();

const matchColumn = (headers: string[], keys: string[]): number => {
  for (let i = 0; i < headers.length; i += 1) {
    const h = headers[i].toLowerCase().replace(/\s/g, '');
    if (keys.some((k) => h.includes(k))) return i;
  }
  return -1;
};

// 把区域文本解析成国家区号：优先纯数字（允许 +/00 前缀），否则查地区名表
export const parseCountryCode = (regionRaw: string): number | null => {
  const raw = regionRaw.trim();
  if (!raw) return null;
  const digits = raw.replace(/^(\+|00)/, '').replace(/\s/g, '');
  if (/^\d+$/.test(digits)) {
    const n = Number(digits);
    return n > 0 ? n : null;
  }
  const byName = REGION_NAME_TO_CODE[raw.toLowerCase()];
  return byName ?? null;
};

export interface ParseResult {
  rows: SmsRow[];
  // 是否识别到各列
  hasPhoneColumn: boolean;
  hasRegionColumn: boolean;
  hasMessageColumn: boolean;
  // 整体错误（如空表、缺必需列）；为空表示表结构正常
  fatalError: string;
}

const EMPTY_RESULT = (fatalError: string): ParseResult => ({
  rows: [],
  hasPhoneColumn: false,
  hasRegionColumn: false,
  hasMessageColumn: false,
  fatalError,
});

// 解析 Excel 文件为短信行列表。表头在第一行，支持中英文列名。
export const parseSmsExcel = async (file: File): Promise<ParseResult> => {
  const buffer = await file.arrayBuffer();
  const workbook = XLSX.read(buffer, { type: 'array' });
  const sheetName = workbook.SheetNames[0];
  if (!sheetName) return EMPTY_RESULT('无法读取工作表');

  const sheet = workbook.Sheets[sheetName];
  // defval 保证空单元格有占位，blankrows:false 跳过全空行
  const matrix = XLSX.utils.sheet_to_json<unknown[]>(sheet, {
    header: 1,
    defval: '',
    blankrows: false,
  });
  if (matrix.length < 2) return EMPTY_RESULT('表格为空或只有表头，没有数据行');

  const headers = (matrix[0] as unknown[]).map(norm);
  const phoneCol = matchColumn(headers, PHONE_KEYS);
  const regionCol = matchColumn(headers, REGION_KEYS);
  const messageCol = matchColumn(headers, MESSAGE_KEYS);

  if (phoneCol === -1) {
    return EMPTY_RESULT('未识别到手机号列（表头需包含「手机号/号码/phone」等）');
  }

  const rows: SmsRow[] = [];
  for (let i = 1; i < matrix.length; i += 1) {
    const cells = matrix[i] as unknown[];
    const phone = norm(cells[phoneCol]).replace(/\s/g, '');
    const regionRaw = regionCol === -1 ? '' : norm(cells[regionCol]);
    const message = messageCol === -1 ? '' : norm(cells[messageCol]);
    // 整行空白跳过
    if (!phone && !regionRaw && !message) continue;

    const countryCode = parseCountryCode(regionRaw);
    let error = '';
    if (!phone) {
      error = '手机号为空';
    } else if (!/^\d+$/.test(phone)) {
      error = '手机号含非数字字符';
    } else if (regionCol !== -1 && regionRaw && countryCode === null) {
      error = `区域「${regionRaw}」无法识别为国家区号`;
    }

    rows.push({
      rowNo: i + 1,
      regionRaw,
      countryCode,
      phone,
      message,
      error,
    });
  }

  if (rows.length === 0) return EMPTY_RESULT('没有解析到有效数据行');

  return {
    rows,
    hasPhoneColumn: phoneCol !== -1,
    hasRegionColumn: regionCol !== -1,
    hasMessageColumn: messageCol !== -1,
    fatalError: '',
  };
};
